import React from 'react'
import { COLORS } from '../theme'

const trim = (n) => String(Math.round(n * 100) / 100)

// "under 1 h", "over 2 MRs per developer a week", "under 1%"
function amount(n, unit) {
    if (unit === '%') return `${trim(n)}%`
    return `${trim(n)} ${unit === 'hours' ? 'h' : unit}`
}

function GoalLine({ goal }) {
    const target = `${goal.direction === 'down' ? 'under' : 'over'} ${amount(goal.targetLimit, goal.unit)}`
    const named = `${goal.target}${goal.targetShare ? `, ${goal.targetShare}` : ''}`
    const color = goal.atGoal === null ? COLORS.textMuted : goal.atGoal ? COLORS.success : COLORS.warning
    const mine =
        goal.ours === null
            ? 'Not measured on this basis yet.'
            : goal.atGoal
              ? `At goal: ${amount(goal.ours, goal.unit)}.`
              : `Now ${goal.band}${goal.bandShare ? ` (${goal.bandShare})` : ''}: ${amount(goal.ours, goal.unit)}.`
    const caveat = [
        goal.ours !== null && goal.stat && (
            goal.stat === 'p75' ? 'This reads our p75, as the band does; the large number above is the median.'
            : goal.stat === 'p50' && goal.aggregation === 'p75' ? 'Our median against a p75 band, which flatters us until our p75 is recorded.'
            : goal.stat),
        goal.derived && 'The report has no band for this span: its phases added up.',
        goal.note,
    ].filter(Boolean).join(' ')
    return (
        <div>
            <div style={{ fontSize: 12.5, lineHeight: 1.45 }}>
                <span style={{ color: COLORS.textMuted }}>Goal {target}. </span>
                <span style={{ color, fontWeight: 600 }}>{mine}</span>
            </div>
            <div style={{ fontSize: 10.5, color: COLORS.textMuted, lineHeight: 1.4 }}>
                {goal.standard}
                {goal.measure ? `, ${goal.measure}` : ''} ({named}).{caveat ? ` ${caveat}` : ''}
            </div>
        </div>
    )
}

function HistoryLine({ history }) {
    const span = history.span > 1 ? `${history.span}-week average ` : ''
    const color =
        history.verdict === 'worse' ? COLORS.danger : history.verdict === 'better' ? COLORS.success : COLORS.textMuted
    const words =
        history.verdict === 'worse'
            ? `Worse than ${history.worse} of the last ${history.of} ${span ? 'readings' : 'weeks'}`
            : history.verdict === 'better'
              ? `Better than ${history.better} of the last ${history.of} ${span ? 'readings' : 'weeks'}`
              : `About usual: better than ${history.better} of the last ${history.of} ${span ? 'readings' : 'weeks'}`
    return (
        <div style={{ fontSize: 12.5, lineHeight: 1.45, color, fontWeight: history.verdict === 'usual' ? 400 : 600 }}>
            {span && <span style={{ fontWeight: 400 }}>{span[0].toUpperCase() + span.slice(1).trim()}: </span>}
            {span ? words[0].toLowerCase() + words.slice(1) : words}.
            {history.note && <span style={{ color: COLORS.textMuted, fontWeight: 400, fontSize: 11 }}> {history.note}</span>}
        </div>
    )
}

// Is this good, against a goal and against ourselves; and when it is not, where
// the number sits, so there is somewhere to start digging.
export default function Standing({ standing }) {
    if (!standing) return null
    const { goal, history, why } = standing
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {goal && <GoalLine goal={goal} />}
            {history && <HistoryLine history={history} />}
            {why && (
                <div style={{ borderLeft: `2px solid ${COLORS.warning}`, paddingLeft: 9, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.textMuted }}>
                        Where to look
                    </div>
                    {why.lines.map((line) => (
                        <div key={line} style={{ fontSize: 12.5, color: COLORS.text, lineHeight: 1.45 }}>{line}</div>
                    ))}
                    {why.dig && (
                        <a href={why.dig.href} target="_blank" rel="noreferrer" style={{ fontSize: 12.5, color: COLORS.primary }}>
                            {why.dig.label} →
                        </a>
                    )}
                </div>
            )}
        </div>
    )
}
